import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../services/api';
import './ApplicationDependencies.css';
import EditScheduleModal from '../components/EditScheduleModal';

interface IntegrationGroup {
  group_id: string;
  group_name: string;
  application_list: string;
  earliest_start_date: string;
  applications: string[];
}

interface ScheduleRecord {
  schedule_id: number;
  application_id: string;
  application_name: string;
  integration_group_id: string;
  integration_group_name: string;
  group_effective_from?: string;
  planned_start_date: string;
  planned_end_date: string;
  schedule_status: string;
  total_duration_days: number;
  total_effort_days: number;
  calculated_priority_score: number;
  phase_assessment_start: string;
  phase_assessment_end: string;
  phase_design_start: string;
  phase_design_end: string;
  phase_migration_start: string;
  phase_migration_end: string;
  phase_testing_start: string;
  phase_testing_end: string;
  phase_golive_start: string;
  phase_golive_end: string;
  resource_constraint_flag: number;
  resource_constraint_count?: number;
  dependency_constraint_flag: number;
  recommendation?: string;
}

interface GanttPhase {
  name: string;
  base_duration: number;
  buffer_days: number;
  total_duration: number;
  start_date: string;
  end_date: string;
}

interface GanttData {
  id: string;
  text: string;
  start_date: string;
  end_date: string;
  phases: GanttPhase[];
  schedule_status: string;
  application_status: string;
  integration_group_id: string;
  integration_group_name?: string;
  recommendation?: string;
}

const PHASE_COLORS: Record<string, string> = {
  'Assessment & Planning': '#2196F3',
  'Design & Architecture': '#9C27B0',
  'Migration Execution': '#4CAF50',
  'Testing & Validation': '#FF9800',
  'Go-Live & Optimization': '#F44336'
};

// 4R Disposition Model Colors (matching Integration pattern)
const getRecommendationColor = (recommendation: string | undefined): string => {
  switch (recommendation) {
    case 'Retire': return '#dc2626';      // Red
    case 'Re-Platform': return '#f59e0b'; // Amber/Orange
    case 'Re-Design': return '#3b82f6';   // Blue
    case 'Retain': return '#10b981';      // Green
    default: return '#6b7280';            // Gray for unknown
  }
};

const RoadmapSchedule: React.FC = () => {
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<IntegrationGroup | null>(null);
  const [scheduleRecords, setScheduleRecords] = useState<ScheduleRecord[]>([]);
  const [ganttData, setGanttData] = useState<GanttData[]>([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'gantt'>('table');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ScheduleRecord | null>(null);
  const [viewOnlyMode, setViewOnlyMode] = useState(false);

  // Track deployment statuses for all applications (from deployment_status table)
  const [deploymentStatuses, setDeploymentStatuses] = useState<Map<string, string>>(new Map());

  // Load integration groups and deployment statuses on mount
  useEffect(() => {
    fetchIntegrationGroups();
    fetchDeploymentStatuses();
  }, []);

  // Load schedule when group changes
  useEffect(() => {
    if (selectedGroupId) {
      if (selectedGroupId === 'ALL') {
        setSelectedGroup(null);
      } else {
        const group = integrationGroups.find(g => g.group_id === selectedGroupId);
        setSelectedGroup(group || null);
      }
      fetchScheduleData();
      if (viewMode === 'gantt') {
        fetchGanttData();
      }
    } else {
      setSelectedGroup(null);
      setScheduleRecords([]);
      setGanttData([]);
    }
  }, [selectedGroupId, viewMode, integrationGroups]);

  const fetchIntegrationGroups = async () => {
    try {
      const response = await authFetch('/api/integration-groups?exclude_analysis=true');
      const result = await response.json();
      if (result.success) {
        setIntegrationGroups(result.data || []);
      }
    } catch (err) {
      console.error('Error fetching integration groups:', err);
    }
  };

  // Fetch deployment statuses from deployment_status table
  const fetchDeploymentStatuses = async () => {
    try {
      const response = await authFetch('/api/deployment-status');
      const result = await response.json();
      // API returns array directly, not wrapped in { success, data }
      const dataArray = Array.isArray(result) ? result : (result.data || []);
      const statusMap = new Map<string, string>();
      dataArray.forEach((r: any) => {
        if (r.application_id && r.deployment_status) {
          statusMap.set(r.application_id, r.deployment_status);
        }
      });
      setDeploymentStatuses(statusMap);
    } catch (err) {
      console.error('Error fetching deployment statuses:', err);
    }
  };

  // Helper to get status color matching DispositionControl
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Need Approval': return { bg: '#fef3c7', color: '#92400e' };
      case 'Approved': return { bg: '#dbeafe', color: '#1e40af' };
      case 'In Development': return { bg: '#e0e7ff', color: '#3730a3' };
      case 'Deployed': return { bg: '#d1fae5', color: '#065f46' };
      default: return { bg: '#f5f5f5', color: '#757575' };
    }
  };

  // Helper to check if editing is disabled (only editable for Need Approval status)
  const isEditDisabled = (applicationId: string) => {
    const status = deploymentStatuses.get(applicationId);
    return status && status !== 'Need Approval';
  };

  const fetchScheduleData = async () => {
    if (!selectedGroupId) return;

    setLoading(true);
    setError(null);

    // Always refresh deployment statuses when fetching schedule data
    fetchDeploymentStatuses();

    try {
      // If "ALL" is selected, fetch without group filter
      const url = selectedGroupId === 'ALL'
        ? '/api/roadmap-schedule'
        : `/api/roadmap-schedule?integration_group_id=${selectedGroupId}`;
      const response = await authFetch(url);
      const result = await response.json();

      if (result.success) {
        setScheduleRecords(result.data || []);
      } else {
        setError(result.error || 'Failed to fetch schedule data');
      }
    } catch (err: any) {
      setError(err.message || 'Error fetching schedule data');
    } finally {
      setLoading(false);
    }
  };

  const fetchGanttData = async () => {
    if (!selectedGroupId) return;

    try {
      // If "ALL" is selected, fetch without group filter
      const url = selectedGroupId === 'ALL'
        ? '/api/roadmap-schedule/visualization/gantt'
        : `/api/roadmap-schedule/visualization/gantt?integration_group_id=${selectedGroupId}`;
      const response = await authFetch(url);
      const result = await response.json();

      if (result.success) {
        setGanttData(result.data || []);
      }
    } catch (err) {
      console.error('Error fetching Gantt data:', err);
    }
  };

  const handleCalculateSchedule = async () => {
    if (!selectedGroupId) {
      setError('Please select an integration group first');
      return;
    }

    setCalculating(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await authFetch('/api/roadmap-schedule/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ integration_group_id: selectedGroupId })
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage(`Schedule calculated successfully for ${result.count} applications`);
        fetchScheduleData();
        if (viewMode === 'gantt') {
          fetchGanttData();
        }
      } else {
        setError(result.error || 'Failed to calculate schedule');
      }
    } catch (err: any) {
      setError(err.message || 'Error calculating schedule');
    } finally {
      setCalculating(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  // Calculate Gantt chart dimensions
  const getGanttDimensions = useCallback(() => {
    if (ganttData.length === 0) return { minDate: new Date(), maxDate: new Date(), totalDays: 0 };

    const allDates = ganttData.flatMap(item => [
      new Date(item.start_date),
      new Date(item.end_date)
    ]);

    const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));

    minDate.setDate(minDate.getDate() - 7);
    maxDate.setDate(maxDate.getDate() + 14);

    const totalDays = Math.ceil((maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));

    return { minDate, maxDate, totalDays };
  }, [ganttData]);

  const renderGanttChart = () => {
    const { minDate, maxDate, totalDays } = getGanttDimensions();
    const chartWidth = Math.max(800, totalDays * 3);

    const getPositionPercent = (dateStr: string) => {
      const date = new Date(dateStr);
      const daysDiff = Math.ceil((date.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));
      return (daysDiff / totalDays) * 100;
    };

    const getWidthPercent = (startStr: string, endStr: string) => {
      const start = new Date(startStr);
      const end = new Date(endStr);
      const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      return Math.max(0.5, (days / totalDays) * 100);
    };

    const monthHeaders: { month: string; startPercent: number; widthPercent: number }[] = [];
    const current = new Date(minDate);
    while (current <= maxDate) {
      const monthStart = new Date(current);
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
      const effectiveEnd = monthEnd > maxDate ? maxDate : monthEnd;

      const startPercent = getPositionPercent(monthStart.toISOString().split('T')[0]);
      const endPercent = getPositionPercent(effectiveEnd.toISOString().split('T')[0]);

      monthHeaders.push({
        month: monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        startPercent,
        widthPercent: endPercent - startPercent
      });

      current.setMonth(current.getMonth() + 1);
      current.setDate(1);
    }

    const today = new Date();
    const todayPercent = getPositionPercent(today.toISOString().split('T')[0]);
    const showTodayLine = todayPercent >= 0 && todayPercent <= 100;

    return (
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'flex' }}>
          {/* Fixed Columns - Grouping ID and Application */}
          <div style={{ flexShrink: 0, borderRight: '2px solid #e0e0e0' }}>
            {/* Column Headers */}
            <div style={{ display: 'flex', height: '30px', borderBottom: '1px solid #e0e0e0', marginBottom: '8px', backgroundColor: '#f8fafc' }}>
              <div style={{
                width: '180px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                textAlign: 'center',
                borderRight: '1px solid #e0e0e0'
              }}>
                Grouping ID
              </div>
              <div style={{
                width: '220px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                textAlign: 'center'
              }}>
                Application
              </div>
            </div>

            {/* Data Rows */}
            {ganttData.map((item, rowIdx) => (
              <div
                key={`info-${item.id}`}
                style={{
                  display: 'flex',
                  height: '40px',
                  borderBottom: '1px solid #f0f0f0',
                  backgroundColor: rowIdx % 2 === 0 ? '#fafafa' : 'white'
                }}
              >
                {/* Grouping ID Column */}
                <div style={{
                  width: '180px',
                  padding: '4px 12px',
                  borderRight: '1px solid #f0f0f0',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  textAlign: 'left'
                }}>
                  <div style={{ fontWeight: '600', color: '#111827', fontSize: '12px', textAlign: 'left' }}>
                    {item.integration_group_id}
                  </div>
                  {item.integration_group_name && (
                    <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                      {item.integration_group_name}
                    </div>
                  )}
                </div>

                {/* Application Column */}
                <div style={{
                  width: '220px',
                  padding: '4px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  textAlign: 'left'
                }}>
                  <div style={{ fontWeight: '600', color: getRecommendationColor(item.recommendation || scheduleRecords.find(r => r.application_id === item.id)?.recommendation), fontSize: '12px', textAlign: 'left' }}>
                    {item.id}
                  </div>
                  <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                    {item.text}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Scrollable Gantt Chart Area */}
          <div style={{ flex: 1, minWidth: chartWidth, padding: '0 16px' }}>
            {/* Month Headers */}
            <div style={{ position: 'relative', height: '30px', borderBottom: '1px solid #e0e0e0', marginBottom: '8px' }}>
              {monthHeaders.map((mh, idx) => (
                <div
                  key={idx}
                  style={{
                    position: 'absolute',
                    left: `${mh.startPercent}%`,
                    width: `${mh.widthPercent}%`,
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#666',
                    borderRight: '1px solid #e0e0e0',
                    paddingTop: '6px'
                  }}
                >
                  {mh.month}
                </div>
              ))}
            </div>

            {/* Gantt Rows */}
            {ganttData.map((item, rowIdx) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '40px',
                  borderBottom: '1px solid #f0f0f0',
                  backgroundColor: rowIdx % 2 === 0 ? '#fafafa' : 'white'
                }}
              >
                <div style={{ flex: 1, position: 'relative', height: '100%' }}>
                {showTodayLine && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${todayPercent}%`,
                      top: 0,
                      bottom: 0,
                      width: '2px',
                      backgroundColor: '#ef4444',
                      zIndex: 10
                    }}
                  />
                )}

                {item.phases.map((phase, phaseIdx) => {
                  if (!phase.start_date || !phase.end_date) return null;
                  const left = getPositionPercent(phase.start_date);
                  const width = getWidthPercent(phase.start_date, phase.end_date);

                  return (
                    <div
                      key={phaseIdx}
                      title={`${phase.name}\n${formatDate(phase.start_date)} - ${formatDate(phase.end_date)}`}
                      style={{
                        position: 'absolute',
                        left: `${left}%`,
                        width: `${width}%`,
                        top: '8px',
                        height: '24px',
                        backgroundColor: PHASE_COLORS[phase.name] || '#999',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        opacity: 0.9
                      }}
                    />
                  );
                })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px'
        }}>
          {Object.entries(PHASE_COLORS).map(([name, color]) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '16px', height: '16px', backgroundColor: color, borderRadius: '3px' }} />
              <span style={{ fontSize: '12px', color: '#666' }}>{name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Table styles matching the screenshot
  const tableStyles = {
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      fontSize: '14px',
      backgroundColor: 'white'
    },
    headerRow: {
      backgroundColor: '#f8fafc',
      borderBottom: '1px solid #e2e8f0'
    },
    headerCell: {
      padding: '14px 12px',
      textAlign: 'left' as const,
      fontWeight: '600',
      color: '#374151',
      fontSize: '14px',
      borderBottom: '1px solid #e2e8f0',
      whiteSpace: 'nowrap' as const
    },
    headerCellCenter: {
      padding: '14px 12px',
      textAlign: 'center' as const,
      fontWeight: '600',
      color: '#374151',
      fontSize: '14px',
      borderBottom: '1px solid #e2e8f0',
      whiteSpace: 'nowrap' as const
    },
    bodyRow: {
      borderBottom: '1px solid #f1f5f9'
    },
    cell: {
      padding: '16px 12px',
      textAlign: 'left' as const,
      verticalAlign: 'top' as const,
      borderBottom: '1px solid #f1f5f9',
      fontSize: '14px'
    },
    cellCenter: {
      padding: '16px 12px',
      textAlign: 'center' as const,
      verticalAlign: 'middle' as const,
      borderBottom: '1px solid #f1f5f9',
      fontSize: '14px'
    }
  };

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Header */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Roadmap Schedule</h1>
            <p className="page-header__subtitle">
              Plan and manage application schedules across integration groups
            </p>
          </div>
          <div className="page-header__actions">
            <button
              onClick={handleCalculateSchedule}
              disabled={!selectedGroupId || selectedGroupId === 'ALL' || calculating}
              style={{
                padding: '8px 16px',
                backgroundColor: calculating || !selectedGroupId || selectedGroupId === 'ALL' ? '#94a3b8' : '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: calculating || !selectedGroupId || selectedGroupId === 'ALL' ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {calculating ? 'Calculating...' : 'Calculate Schedule'}
            </button>
          </div>
        </div>
      </div>

      {/* Controls Card - Sticky below header */}
      <div style={{ flexShrink: 0, padding: '0 16px', marginBottom: '16px' }}>
        <div className="md-card">
          <div className="md-card-content" style={{ padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              {/* Integration Group Selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '16px', fontWeight: '500', color: '#374151' }}>
                  Integration Group:
                </label>
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '16px',
                    minWidth: '300px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Integration Group...</option>
                  <option value="ALL">All Integration Groups</option>
                  {integrationGroups.map(group => (
                    <option key={group.group_id} value={group.group_id}>
                      {group.group_id} - {group.group_name} ({group.applications?.length || 0} apps)
                    </option>
                  ))}
                </select>
              </div>

              {/* View Mode Toggle */}
              <div style={{ display: 'flex', gap: '4px', backgroundColor: '#f1f5f9', borderRadius: '6px', padding: '2px' }}>
                <button
                  onClick={() => setViewMode('table')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '16px',
                    fontWeight: '500',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: viewMode === 'table' ? 'white' : 'transparent',
                    color: viewMode === 'table' ? '#1976d2' : '#64748b',
                    boxShadow: viewMode === 'table' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  Table View
                </button>
                <button
                  onClick={() => setViewMode('gantt')}
                  style={{
                    padding: '6px 12px',
                    fontSize: '16px',
                    fontWeight: '500',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: viewMode === 'gantt' ? 'white' : 'transparent',
                    color: viewMode === 'gantt' ? '#1976d2' : '#64748b',
                    boxShadow: viewMode === 'gantt' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  Gantt Chart
                </button>
              </div>

              {/* Stats */}
              {scheduleRecords.length > 0 && (
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '16px', fontSize: '13px', color: '#64748b' }}>
                  <span><strong>{scheduleRecords.length}</strong> applications</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Data Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>
        {/* Messages */}
        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            color: '#dc2626',
            fontSize: '13px',
            marginBottom: '16px'
          }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            color: '#16a34a',
            fontSize: '13px',
            marginBottom: '16px'
          }}>
            {successMessage}
          </div>
        )}

        {/* Main Content */}
        <div className="md-card">
          <div className="md-card-content" style={{ padding: '0' }}>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                Loading schedule data...
              </div>
            ) : !selectedGroupId ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
                <p style={{ color: '#666', fontSize: '14px' }}>
                  Select an integration group to view or calculate the roadmap schedule
                </p>
              </div>
            ) : viewMode === 'table' ? (
              /* Table View - Matching Screenshot Layout */
              scheduleRecords.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                  <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
                    No schedule data found for this integration group
                  </p>
                  <button
                    onClick={handleCalculateSchedule}
                    disabled={calculating}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Calculate Schedule Now
                  </button>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={tableStyles.table}>
                    <thead>
                      <tr style={tableStyles.headerRow}>
                        <th style={{ ...tableStyles.headerCellCenter, minWidth: '280px' }}>Grouping ID</th>
                        <th style={{ ...tableStyles.headerCellCenter, minWidth: '140px' }}>Grouping Dates</th>
                        <th style={{ ...tableStyles.headerCellCenter, minWidth: '280px' }}>Application</th>
                        <th style={{ ...tableStyles.headerCellCenter, minWidth: '80px' }}>Effort</th>
                        <th style={{ ...tableStyles.headerCellCenter, minWidth: '100px' }}>Start Date</th>
                        <th style={{ ...tableStyles.headerCellCenter, minWidth: '100px' }}>End Date</th>
                        <th style={{ ...tableStyles.headerCellCenter, minWidth: '90px' }}>Duration</th>
                        <th style={{ ...tableStyles.headerCellCenter, minWidth: '100px' }}>Status</th>
                        <th style={{ ...tableStyles.headerCellCenter, minWidth: '80px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scheduleRecords.map((record) => (
                        <tr key={record.application_id} style={tableStyles.bodyRow}>
                          {/* Grouping ID Column */}
                          <td style={tableStyles.cell}>
                            <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                              {record.integration_group_id}
                            </div>
                            <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.4' }}>
                              {record.integration_group_name}
                            </div>
                          </td>

                          {/* Grouping Dates Column */}
                          <td style={tableStyles.cell}>
                            <div style={{ fontSize: '14px', color: '#374151' }}>
                              <span style={{ color: '#6b7280' }}>From:</span> {formatDate(record.group_effective_from || selectedGroup?.earliest_start_date || null)}
                            </div>
                            <div style={{ fontSize: '14px', color: '#374151', marginTop: '2px' }}>
                              <span style={{ color: '#6b7280' }}>To:</span> {formatDate(record.planned_end_date)}
                            </div>
                          </td>

                          {/* Application Column */}
                          <td style={tableStyles.cell}>
                            <div style={{
                              fontWeight: '600',
                              color: getRecommendationColor(record.recommendation),
                              marginBottom: '4px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}>
                              {record.application_id}
                            </div>
                            <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.4' }}>
                              {record.application_name}
                              {record.recommendation && (
                                <span style={{
                                  marginLeft: '8px',
                                  padding: '2px 6px',
                                  backgroundColor: record.recommendation === 'Retire' ? '#fef2f2' :
                                                   record.recommendation === 'Retain' ? '#f0fdf4' : '#eff6ff',
                                  color: record.recommendation === 'Retire' ? '#dc2626' :
                                         record.recommendation === 'Retain' ? '#16a34a' : '#2563eb',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: '500'
                                }}>
                                  {record.recommendation}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Effort Column */}
                          <td style={tableStyles.cellCenter}>
                            <span style={{ fontWeight: '500', color: '#374151' }}>
                              {record.total_effort_days} days
                            </span>
                          </td>

                          {/* Start Date Column */}
                          <td style={tableStyles.cellCenter}>
                            <span style={{ color: '#374151' }}>
                              {formatDate(record.planned_start_date)}
                            </span>
                          </td>

                          {/* End Date Column */}
                          <td style={tableStyles.cellCenter}>
                            <span style={{ color: '#374151' }}>
                              {formatDate(record.planned_end_date)}
                            </span>
                          </td>

                          {/* Duration Column */}
                          <td style={tableStyles.cellCenter}>
                            <span style={{ fontWeight: '500', color: '#374151' }}>
                              {record.total_duration_days} days
                            </span>
                          </td>

                          {/* Status Column */}
                          <td style={tableStyles.cellCenter}>
                            {(() => {
                              const status = deploymentStatuses.get(record.application_id) || 'Not Set';
                              const statusColors = getStatusColor(status);
                              return (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '6px 12px',
                                  borderRadius: '16px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  backgroundColor: statusColors.bg,
                                  color: statusColors.color
                                }}>
                                  {status}
                                </span>
                              );
                            })()}
                          </td>

                          {/* Actions Column */}
                          <td style={tableStyles.cellCenter}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                              <button
                                onClick={() => {
                                  setEditingRecord(record);
                                  setViewOnlyMode(true);
                                  setEditModalOpen(true);
                                }}
                                title="View details"
                                style={{
                                  padding: '4px 6px',
                                  backgroundColor: 'transparent',
                                  color: '#1976d2',
                                  border: '1px solid #1976d2',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  minWidth: 'auto'
                                }}
                              >
                                👁️
                              </button>
                              {!isEditDisabled(record.application_id) && (
                                <button
                                  onClick={() => {
                                    setEditingRecord(record);
                                    setViewOnlyMode(false);
                                    setEditModalOpen(true);
                                  }}
                                  title="Edit schedule"
                                  style={{
                                    padding: '4px 6px',
                                    backgroundColor: 'transparent',
                                    color: '#1976d2',
                                    border: '1px solid #1976d2',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    minWidth: 'auto'
                                  }}
                                >
                                  ✏️
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              /* Gantt Chart View */
              ganttData.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                  <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
                    No schedule data to display in Gantt chart
                  </p>
                  <button
                    onClick={handleCalculateSchedule}
                    disabled={calculating}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#1976d2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Calculate Schedule Now
                  </button>
                </div>
              ) : (
                renderGanttChart()
              )
            )}
          </div>
        </div>
      </div>

      {/* Edit Schedule Modal */}
      {editingRecord && (
        <EditScheduleModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditingRecord(null);
            setViewOnlyMode(false);
          }}
          onSave={() => {
            // Refresh data after save
            fetchScheduleData();
            fetchDeploymentStatuses();
            if (viewMode === 'gantt') {
              fetchGanttData();
            }
            setSuccessMessage('Schedule updated successfully');
            setTimeout(() => setSuccessMessage(null), 3000);
          }}
          applicationId={editingRecord.application_id}
          applicationName={editingRecord.application_name}
          integrationGroupId={editingRecord.integration_group_id}
          currentStartDate={editingRecord.planned_start_date}
          currentEndDate={editingRecord.planned_end_date}
          viewOnly={viewOnlyMode}
        />
      )}
    </div>
  );
};

export default RoadmapSchedule;
